import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Upload, CheckCircle2, AlertTriangle, FileSpreadsheet, ShieldCheck, History } from "lucide-react";
import { DspSurface, PLATFORMS } from "../UserDsp";

const STATUS_STYLE = {
  new:            { label: "New DSP Record", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  update:         { label: "Existing – Update", color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30" },
  needs_review:   { label: "Needs Review", color: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  invalid:        { label: "Invalid", color: "bg-rose-500/10 text-rose-400 border-rose-500/30" },
};

export default function AdminUserDsp() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get(`/admin/users/${userId}`).then((r) => setUser(r.data)).catch(() => {});
  }, [userId]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <button onClick={() => nav("/admin/users")}
        data-testid="admin-dsp-back"
        className="text-sm text-zinc-400 hover:text-cyan-400 inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to Users
      </button>
      <div className="mt-2 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Administrator · Users</div>
          <h1 className="mt-1 text-3xl font-bold flex items-center gap-3">DSP Links</h1>
          {user && (
            <div className="mt-2 text-sm text-zinc-400 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              Scoped to <span className="text-zinc-100 font-medium" data-testid="admin-dsp-target">{user.full_name}</span> · {user.email}
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue="records" className="mt-8">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="records" data-testid="admin-dsp-tab-records">Records & Analytics</TabsTrigger>
          <TabsTrigger value="import" data-testid="admin-dsp-tab-import"><Upload className="w-3.5 h-3.5 mr-1.5" /> Import Links Excel</TabsTrigger>
          <TabsTrigger value="history" data-testid="admin-dsp-tab-history"><History className="w-3.5 h-3.5 mr-1.5" /> Import History</TabsTrigger>
        </TabsList>
        <TabsContent value="records"><DspSurface scope="admin" userId={userId} /></TabsContent>
        <TabsContent value="import"><ImportFlow userId={userId} /></TabsContent>
        <TabsContent value="history"><HistoryTab userId={userId} /></TabsContent>
      </Tabs>
    </div>
  );
}

function ImportFlow({ userId }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [overwrite, setOverwrite] = useState({}); // { row_index: [platform,...] }
  const fileRef = useRef(null);

  const reset = () => {
    setFile(null); setPreview(null); setResult(null); setOverwrite({});
    if (fileRef.current) fileRef.current.value = "";
  };

  const run = async () => {
    if (!file) return toast.error("Select an Excel file");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/user-dsp/${userId}/preview`, fd,
        { headers: { "Content-Type": "multipart/form-data" }});
      setPreview(data);
      toast.success(`Parsed ${data.counters.total} rows from sheet "${data.detection.sheet}"`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Preview failed"); }
    finally { setLoading(false); }
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      const body = { preview_id: preview.preview_id, overwrite };
      const { data } = await api.post(`/admin/user-dsp/${userId}/confirm`, body);
      setResult(data);
      toast.success(`Applied — ${data.created} new, ${data.updated} updated, ${data.kept_conflicts} conflicts kept`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Confirm failed"); }
    finally { setConfirming(false); }
  };

  if (result) return (
    <div className="mt-6 p-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/5">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="w-8 h-8 text-emerald-400" />
        <div>
          <div className="text-lg font-semibold">Import complete</div>
          <div className="text-sm text-zinc-400">
            {result.created} new · {result.updated} updated · {result.kept_conflicts} conflicts kept · {result.invalid} invalid
          </div>
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <Button onClick={reset} data-testid="admin-dsp-new-import"
          className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">
          Import another Excel
        </Button>
      </div>
    </div>
  );

  if (!preview) return (
    <div className="mt-6 space-y-6">
      <div className="p-6 rounded-2xl border border-zinc-800 bg-zinc-950/60">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-cyan-400"><FileSpreadsheet className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-semibold">Upload this user's DSP Links Excel</div>
            <div className="text-sm text-zinc-500 mt-1">
              Any workbook layout — we auto-detect the correct sheet + header row.
              Supported columns: SONG · FILM/ALBUM · LANGUAGE · Track no. · UPC ID · DATE OF MUSIC RELEASE · ISRC · LABEL · LYRICIST · COMPOSER · ARTIST1/ Singer · link_amazonMusic · link_appleMusic · link_gaana · link_instagram · link_jioSaavn · link_shazam · link_soundcloud · link_spotify · link_youtube · link_youtubeMusic
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="admin-dsp-file"
              className="mt-3 text-xs text-zinc-300 file:mr-3 file:rounded-full file:border-0 file:bg-zinc-800 file:px-3 file:py-1.5 file:text-zinc-100 hover:file:bg-zinc-700"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={run} disabled={!file || loading} data-testid="admin-dsp-preview-btn"
          className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">
          <Upload className="w-4 h-4 mr-2" />{loading ? "Analyzing…" : "Preview import"}
        </Button>
      </div>
    </div>
  );

  const c = preview.counters;
  const d = preview.detection;
  return (
    <div className="mt-6 space-y-6">
      <div className="p-4 rounded-2xl border border-zinc-800 bg-zinc-950/60 text-xs">
        <div className="font-semibold text-zinc-300 mb-2">Auto-detected</div>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-zinc-400">
          <div>Sheet: <span className="text-cyan-400 font-mono" data-testid="admin-dsp-detected-sheet">{d.sheet}</span></div>
          <div>Header row: <span className="text-cyan-400 font-mono">{d.header_row_index + 1}</span></div>
          <div className="md:col-span-2">Detected columns: <span className="text-emerald-400 font-mono text-[11px]">{d.headers.join(" · ")}</span></div>
          {d.unknown_link_headers?.length > 0 && (
            <div className="text-amber-400">Unknown link_* columns ignored: {d.unknown_link_headers.join(", ")}</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Stat k="Total rows" v={c.total} testid="admin-dsp-stat-total" />
        <Stat k="New" v={c.new} tone="emerald" testid="admin-dsp-stat-new" />
        <Stat k="Update" v={c.update} tone="cyan" testid="admin-dsp-stat-update" />
        <Stat k="Needs review" v={c.needs_review} tone="amber" testid="admin-dsp-stat-review" />
        <Stat k="Invalid" v={c.invalid} tone="rose" testid="admin-dsp-stat-invalid" />
        <Stat k="Platforms detected" v={preview.detection.detected_platforms.length} testid="admin-dsp-stat-platforms" />
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="p-3">#</th><th className="p-3">Song</th><th className="p-3">Artist</th>
              <th className="p-3">ISRC / UPC</th><th className="p-3">Platforms</th><th className="p-3">Status / Reason</th>
            </tr>
          </thead>
          <tbody data-testid="admin-dsp-rows">
            {preview.rows.map((r) => {
              const s = STATUS_STYLE[r.status] || STATUS_STYLE.invalid;
              const overwriteSet = new Set(overwrite[r.row_index] || []);
              const toggle = (p) => {
                const next = new Set(overwriteSet);
                if (next.has(p)) next.delete(p); else next.add(p);
                setOverwrite({ ...overwrite, [r.row_index]: Array.from(next) });
              };
              return (
                <tr key={r.row_index} data-testid={`admin-dsp-row-${r.row_index}`} className="border-b border-zinc-900 align-top">
                  <td className="p-3 font-mono text-zinc-500 text-xs">{r.row_index}</td>
                  <td className="p-3">
                    <div className="font-medium">{r.record.song_name || "—"}</div>
                    <div className="text-[11px] text-zinc-500">{r.record.album || ""}</div>
                  </td>
                  <td className="p-3 text-zinc-400">{r.record.artist_singer || "—"}</td>
                  <td className="p-3 font-mono text-[11px] text-zinc-500">
                    <div>{r.record.isrc || "—"}</div>
                    <div>{r.record.upc || "—"}</div>
                  </td>
                  <td className="p-3 text-xs">
                    {r.will_add.length > 0 && <div className="text-emerald-400">+{r.will_add.length}: {r.will_add.join(", ")}</div>}
                    {r.will_conflict.length > 0 && (
                      <div className="mt-1 text-amber-400">
                        {r.will_conflict.length} conflict(s) — kept unless ticked:
                        <div className="mt-1 space-y-0.5">
                          {r.conflicts.map((c) => (
                            <label key={c.platform} className="flex items-start gap-1.5 cursor-pointer">
                              <input type="checkbox" checked={overwriteSet.has(c.platform)} onChange={() => toggle(c.platform)}
                                data-testid={`admin-dsp-overwrite-${r.row_index}-${c.platform}`} className="mt-0.5" />
                              <span className="flex-1">
                                <span className="text-zinc-500">{PLATFORMS[c.platform]?.label || c.platform}:</span>{" "}
                                <span className="text-zinc-500 line-through">{c.old.slice(0, 40)}</span>{" → "}
                                <span className="text-emerald-400">{c.new.slice(0, 40)}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    {r.invalid_links?.length > 0 && (
                      <div className="mt-1 text-rose-400">{r.invalid_links.length} invalid URL cell(s)</div>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 rounded-md text-xs border ${s.color}`}>{s.label}</span>
                    {r.reason && <div className="text-[11px] text-rose-300 mt-1">{r.reason}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={reset} className="border-zinc-700 hover:bg-zinc-900" data-testid="admin-dsp-cancel">Cancel</Button>
        <Button onClick={confirm} disabled={confirming || (c.new + c.update === 0)}
          data-testid="admin-dsp-confirm"
          className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">
          <CheckCircle2 className="w-4 h-4 mr-2" />{confirming ? "Applying…" : `Confirm import (${c.new + c.update})`}
        </Button>
      </div>
    </div>
  );
}

function HistoryTab({ userId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/admin/user-dsp/${userId}/history`).then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, [userId]);
  return (
    <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-x-auto">
      <table className="w-full min-w-[900px] text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <th className="p-3">When</th><th className="p-3">File</th>
            <th className="p-3 text-right">Rows</th><th className="p-3 text-right">New</th>
            <th className="p-3 text-right">Updated</th><th className="p-3 text-right">Invalid</th>
            <th className="p-3">Admin</th>
          </tr>
        </thead>
        <tbody data-testid="admin-dsp-history-tbody">
          {loading && <tr><td colSpan="7" className="p-6 text-center text-zinc-500">Loading…</td></tr>}
          {!loading && rows.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-zinc-500">No imports yet for this user.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} data-testid={`admin-dsp-history-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
              <td className="p-3 text-zinc-400 font-mono text-xs whitespace-nowrap">{new Date(r.imported_at).toLocaleString()}</td>
              <td className="p-3">{r.filename}</td>
              <td className="p-3 text-right font-mono">{r.total_rows}</td>
              <td className="p-3 text-right font-mono text-emerald-400">{r.created}</td>
              <td className="p-3 text-right font-mono text-cyan-400">{r.updated}</td>
              <td className="p-3 text-right font-mono text-rose-400">{r.invalid}</td>
              <td className="p-3 text-xs text-zinc-400">{r.admin_email}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ k, v, tone, testid }) {
  const c = tone === "emerald" ? "text-emerald-400" : tone === "rose" ? "text-rose-400" : tone === "amber" ? "text-amber-400" : tone === "cyan" ? "text-cyan-400" : "text-zinc-100";
  return (
    <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-950/60">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
      <div className={`mt-1.5 text-2xl font-semibold ${c}`} data-testid={testid}>{v}</div>
    </div>
  );
}
